import React, { PropsWithChildren, useState } from 'react';
import { useShow } from '@refinedev/core';
import { useTranslation } from 'react-i18next';
import { Show, DateField } from '@refinedev/mui';
import {
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { ErrorBoundary } from '@/shared/components/ui';
import {
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Update as UpdateIcon,
  Event as EventIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Preview as PreviewIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import {
  EmaQuestionnaireList,
  StudyPreviewModal,
  ExportButton,
  DuplicateStudyButton,
} from '@/features/studies/components';
import { StudyDetail } from '@aphp-ema/common/study';
import { useMe } from '@/core/auth/useRoles';

export const StudyShow: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const {
    query: { data, isLoading },
  } = useShow<StudyDetail>({
    resource: 'admin/studies',
    id,
  });

  const study = data?.data;
  const hasInitialQuestionnaire = study?.initialQuestionnaire !== undefined;
  const numberOfParticipants = study?.targetPopulation?.data?.length;

  const getTemporalStatusIcon = (status: string) => {
    switch (status) {
      case 'ongoing':
        return <AccessTimeIcon />;
      case 'upcoming':
        return <UpdateIcon />;
      case 'past':
        return <EventIcon />;
      default:
        return null;
    }
  };

  const getTemporalStatusColor = (
    status: string,
  ): 'success' | 'info' | 'default' => {
    switch (status) {
      case 'ongoing':
        return 'success';
      case 'upcoming':
        return 'info';
      case 'past':
        return 'default';
      default:
        return 'default';
    }
  };

  const { data: me } = useMe();
  const isAdmin = me?.role === 'admin';
  const canExport = isAdmin || study?.publishedBy === me?.id;

  return (
    <ErrorBoundary>
      <Show
        isLoading={isLoading}
        resource="admin/studies"
        recordItemId={id}
        canDelete={false}
        title={t('pages.studies.show.title')}
        headerButtons={() => (
          <>
            {id && <DuplicateStudyButton studyId={id} variant="outlined" />}
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewModalOpen(true)}
              sx={{ ml: 1 }}
            >
              {t('pages.studies.preview.button')}
            </Button>
            {id && study?.name && canExport && (
              <ExportButton
                studyId={id}
                studyName={study.name}
                study={study}
                variant="outlined"
                size="medium"
              />
            )}
          </>
        )}
      >
        <Box sx={{ maxWidth: 900, margin: '0 auto' }}>
          {}
          <Paper elevation={2} sx={{ p: 4, mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {study?.name}
            </Typography>

            <Box
              sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}
            >
              {study?.status && (
                <Chip
                  label={t(`pages.studies.status.${study.status}`)}
                  color={study.status === 'published' ? 'success' : 'default'}
                  size="medium"
                  icon={<CheckCircleIcon />}
                  sx={{ fontSize: '1rem', py: 0.5 }}
                />
              )}
              {study?.temporalStatus && (
                <Chip
                  label={String(
                    t(
                      `pages.studies.temporalStatus.${study.temporalStatus}`,
                      study.temporalStatus,
                    ),
                  )}
                  color={getTemporalStatusColor(study.temporalStatus)}
                  size="medium"
                  icon={
                    getTemporalStatusIcon(study.temporalStatus) || undefined
                  }
                  sx={{ fontSize: '1rem', py: 0.5 }}
                />
              )}
            </Box>
          </Paper>

          {}
          <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ mb: 3, borderBottom: '1px solid #eee', pb: 1 }}
            >
              {t('pages.studies.sections.basicInfo')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  ID
                </Typography>
                <Typography variant="body1" fontSize="1.1rem" sx={{ mb: 2 }}>
                  {study?.id}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {t('pages.studies.fields.contactName')}
                  </Typography>
                </Box>
                <Typography variant="body1" fontSize="1.1rem" sx={{ mb: 2 }}>
                  {study?.contactName}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="text.secondary"
                  >
                    {t('pages.studies.fields.contactMail')}
                  </Typography>
                </Box>
                <Typography variant="body1" fontSize="1.1rem" sx={{ mb: 2 }}>
                  {study?.contactMail}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {t(
                    'pages.studies.fields.allowParticipantsToViewPreviousResponses.label',
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip
                    label={
                      study?.allowParticipantsToViewPreviousResponses
                        ? t('buttons.yes')
                        : t('buttons.no')
                    }
                    color={
                      study?.allowParticipantsToViewPreviousResponses
                        ? 'success'
                        : 'default'
                    }
                    size="medium"
                    icon={
                      study?.allowParticipantsToViewPreviousResponses ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )
                    }
                    sx={{ fontSize: '0.875rem' }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {}

          <ShowItemContainer
            title={t('pages.studies.sections.description')}
            icon={<DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.secondary"
            >
              {t('pages.studies.fields.shortDescription')}
            </Typography>
            <Typography variant="body1" fontSize="1.1rem" sx={{ mb: 3 }}>
              {study?.shortDescription}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.secondary"
            >
              {t('pages.studies.fields.longDescription')}
            </Typography>
            <Typography variant="body1" fontSize="1.1rem">
              {study?.longDescription}
            </Typography>
          </ShowItemContainer>

          {}

          <ShowItemContainer
            title={t('pages.studies.sections.timeline')}
            icon={<EventIcon sx={{ mr: 1, color: 'primary.main' }} />}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {t('pages.studies.fields.startDate')}
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="1.1rem"
                  sx={{ mb: 2, display: 'block' }}
                >
                  <DateField value={study?.startDate} format="DD/MM/YYYY" />
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {t('pages.studies.fields.endDate')}
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="1.1rem"
                  sx={{ mb: 2, display: 'block' }}
                >
                  <DateField value={study?.endDate} format="DD/MM/YYYY" />
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {t(
                    'pages.studies.fields.subscriptionValidityDayInterval',
                    'Subscription Validity (Days)',
                  )}
                </Typography>
                <Typography variant="body1" fontSize="1.1rem" sx={{ mb: 2 }}>
                  {study?.subscriptionValidityDayInterval}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                >
                  {t('pages.studies.fields.createdAt')}
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  fontSize="1.1rem"
                  sx={{ mb: 2, display: 'block' }}
                >
                  <DateField
                    value={study?.createdAt}
                    format="DD/MM/YYYY HH:mm"
                  />
                </Typography>
              </Grid>
            </Grid>
          </ShowItemContainer>

          {numberOfParticipants && (
            <ShowItemContainer
              title={t('pages.studies.sections.targetPopulation')}
              icon={<PersonIcon sx={{ mr: 1, color: 'primary.main' }} />}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                {t('pages.studies.targetPopulation.numberOfParticipants', {
                  count: numberOfParticipants,
                })}
              </Typography>
            </ShowItemContainer>
          )}

          <ShowItemContainer
            title={t('pages.studies.sections.initialQuestionnaire')}
            icon={<AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />}
          >
            {hasInitialQuestionnaire ? (
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t('pages.studies.initialQuestionnaire.added')}
                </Typography>
                <Button variant="outlined" disabled sx={{ mr: 1 }}>
                  {t('buttons.modify')}
                </Button>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t('pages.studies.initialQuestionnaire.empty')}
                </Typography>
                {}
              </Box>
            )}
          </ShowItemContainer>
          <ShowItemContainer
            title={t('pages.studies.sections.emaQuestionnaires')}
            icon={<AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />}
          >
            <EmaQuestionnaireList
              emaQuestionnaires={study?.emaQuestionnaires}
            />
          </ShowItemContainer>
        </Box>
      </Show>

      {id && study?.name && (
        <StudyPreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          studyId={id}
          studyName={study.name}
        />
      )}
    </ErrorBoundary>
  );
};

const ShowItemContainer = ({
  children,
  title,
  icon,
}: PropsWithChildren<{ title: string; icon: React.ReactNode }>) => {
  return (
    <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        {icon}
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ borderBottom: '1px solid #eee', pb: 1, width: '100%' }}
        >
          {title}
        </Typography>
      </Box>
      <Box>{children}</Box>
    </Paper>
  );
};
